import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { parseTimestamp, rowValue } from '@/_shared/components/Table/utils';

dayjs.extend(customParseFormat);

/**
 * ISO-8601-ish date / datetime, e.g. `2026-06-05`, `2026-06-05 15:45:30`,
 * `2026-06-05T15:45:30.123Z`, `2026-06-05T10:00:00+05:30`. Deliberately strict
 * (requires `YYYY-MM-DD`) so plain numbers / years / ids are not misdetected.
 */
const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function isRealCalendarDate(value: string): boolean {
  return dayjs(value.slice(0, 10), 'YYYY-MM-DD', true).isValid();
}

function looksLikeTimestamp(value: string): boolean {
  return ISO_TIMESTAMP_RE.test(value) && isRealCalendarDate(value) && parseTimestamp(value) !== null;
}

/**
 * True when every non-empty value in the column is a timestamp: a real `Date`
 * object or an ISO-ish string. Numbers and other types are rejected — an epoch number is
 * indistinguishable from ordinary numeric data, so it's not auto-detected.
 */
export function isTimestampColumn<T>(data: T[], id: string): boolean {
  let seen = 0;
  for (const row of data) {
    const value = rowValue(row, id);
    if (value == null || value === '') continue;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return false;
    } else if (typeof value === 'string') {
      if (!looksLikeTimestamp(value)) return false;
    } else {
      return false;
    }
    seen += 1;
  }
  return seen > 0;
}
